class NovaAISuite extends HTMLElement {
  connectedCallback() {
    try { this.cfg = JSON.parse(this.querySelector('[data-ai-config]').textContent); } catch { this.cfg = {}; }
    this.panel = this.querySelector('.nova-ai__panel');
    this.scrim = this.querySelector('.nova-ai__scrim');
    this.messages = this.querySelector('[data-ai-messages]');
    this.logoTemplate = this.querySelector('[data-ai-logo]');
    this.starters = this.querySelector('[data-ai-starters]');
    this.sendButton = this.querySelector('[data-ai-chat-form] button[type="submit"]');
    this.initialMessages = this.messages?.innerHTML || '';
    this.history = [];
    this.querySelector('[data-ai-open]')?.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-ai-close]').forEach(button => button.addEventListener('click', () => this.close()));
    this.querySelector('[data-ai-tools-toggle]')?.addEventListener('click', () => this.toggleTools());
    this.querySelector('[data-ai-reset]')?.addEventListener('click', () => this.resetConversation());
    this.querySelectorAll('[data-ai-tab]').forEach(button => button.addEventListener('click', () => this.selectTab(button.dataset.aiTab)));
    this.querySelector('[data-ai-chat-form]')?.addEventListener('submit', event => this.chat(event));
    const chatInput = this.querySelector('[data-ai-chat-form] textarea');
    chatInput?.addEventListener('input', () => this.resizeComposer(chatInput));
    chatInput?.addEventListener('keydown', event => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        event.currentTarget.form?.requestSubmit();
      }
    });
    this.querySelector('[data-ai-size-form]')?.addEventListener('submit', event => this.size(event));
    this.querySelector('[data-ai-style-form]')?.addEventListener('submit', event => this.style(event));
    this.addEventListener('click', event => {
      const prompt = event.target.closest('[data-ai-question]');
      if (prompt) { this.selectTab('chat'); this.sendQuestion(prompt.dataset.aiQuestion); }
      if (event.target.closest('.nova-ai__product-card')) this.track('recommendation_clicked');
    });
    document.addEventListener('keydown', event => { if (event.key === 'Escape') this.close(); });
    if (this.cfg.personalize && this.cfg.product) this.personalize();
  }

  labels() {
    return /^zh\b/i.test(this.cfg.locale || document.documentElement.lang || '') ? {
      thinking: '正在结合商品和店铺政策筛选…', fallback: '告诉我你最在意的场合、尺码、颜色或预算。',
      selected: 'NO ASK 精选', inStock: '有货', outOfStock: '暂时缺货', why: '推荐理由', view: '查看商品',
      sizes: '可选尺码', source: '店铺政策', read: '查看原文', more: '更多',
      networkError: '暂时无法连接智能导购，请稍后重试。', serviceError: '智能导购暂时不可用，请稍后重试。'
    } : {
      thinking: 'Checking products and store policies…', fallback: 'Tell me the occasion, fit, color or budget that matters most.',
      selected: 'NO ASK PICK', inStock: 'In stock', outOfStock: 'Out of stock', why: 'WHY IT FITS', view: 'View product',
      sizes: 'Sizes', source: 'STORE POLICY', read: 'Read policy', more: 'more',
      networkError: 'The shopping assistant could not connect. Please try again shortly.', serviceError: 'The shopping assistant is temporarily unavailable. Please try again.'
    };
  }

  open() {
    this.panel.hidden = false;
    this.scrim.hidden = false;
    this.dataset.open = 'true';
    this.querySelector('[data-ai-open]')?.setAttribute('aria-expanded', 'true');
    if (matchMedia('(max-width:749px)').matches) {
      this.previousPageOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    this.panel.querySelector('textarea,input')?.focus();
    this.track('ai_opened');
  }

  close() {
    this.panel.hidden = true;
    this.scrim.hidden = true;
    this.dataset.open = 'false';
    this.querySelector('[data-ai-open]')?.setAttribute('aria-expanded', 'false');
    this.closeTools();
    if (this.previousPageOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousPageOverflow;
      this.previousPageOverflow = undefined;
    }
  }

  toggleTools() {
    const tools = this.querySelector('[data-ai-tools]');
    const button = this.querySelector('[data-ai-tools-toggle]');
    if (!tools || !button) return;
    const willOpen = tools.hidden;
    tools.hidden = !willOpen;
    button.setAttribute('aria-expanded', String(willOpen));
  }

  closeTools() {
    const tools = this.querySelector('[data-ai-tools]');
    if (tools) tools.hidden = true;
    this.querySelector('[data-ai-tools-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  resetConversation() {
    this.history = [];
    this._asking = false;
    if (this.messages) this.messages.innerHTML = this.initialMessages;
    if (this.starters) {
      this.starters.hidden = false;
      this.starters.innerHTML = '<button type="button" data-ai-question="Recommend 3 products for my needs and explain the differences.">Find my best match</button><button type="button" data-ai-question="Which products fit my budget and can be returned?">Budget + returns</button><button type="button" data-ai-question="What should I know about shipping before I choose?">Shipping guidance</button>';
    }
    const input = this.querySelector('[data-ai-chat-form] textarea');
    if (input) { input.value = ''; this.resizeComposer(input); input.focus(); }
    this.selectTab('chat');
  }

  resizeComposer(input) {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 104)}px`;
  }

  selectTab(id) {
    this.querySelectorAll('[data-ai-tab]').forEach(button => button.setAttribute('aria-selected', String(button.dataset.aiTab === id)));
    this.querySelectorAll('[data-ai-view]').forEach(view => { view.hidden = view.dataset.aiView !== id; });
    this.closeTools();
  }

  async catalogFor(question) {
    const catalog = Array.isArray(this.cfg.catalog) ? [...this.cfg.catalog] : [];
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(question)}&resources[type]=product&resources[limit]=8`;
      const response = await fetch(url, { headers: { accept: 'application/json' } });
      if (!response.ok) return catalog;
      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      products.forEach(product => catalog.unshift({
        title: product.title,
        handle: product.handle,
        url: product.url,
        vendor: product.vendor,
        type: product.type,
        description: product.body || '',
        tags: product.tags || [],
        priceFormatted: product.price,
        available: product.available !== false,
        image: product.featured_image ? { url: product.featured_image.url || product.featured_image, alt: product.title } : null
      }));
    } catch { /* The embedded catalog remains available when predictive search is unavailable. */ }
    const seen = new Set();
    return catalog.filter(product => product?.handle && !seen.has(product.handle) && seen.add(product.handle)).slice(0, 60);
  }

  async api(kind, payload) {
    const base = String(this.cfg.endpoint || '').replace(/\/$/, '');
    const requestPayload = { ...payload };
    const catalog = requestPayload._catalog || this.cfg.catalog;
    delete requestPayload._catalog;
    const response = await fetch(`${base}/api/${kind}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-nova-token': this.cfg.token },
      body: JSON.stringify({ ...requestPayload, context: {
        locale: this.cfg.locale, currency: this.cfg.currency, brand_voice: this.cfg.brandVoice,
        store: this.cfg.store, product: this.cfg.product, catalog, policies: this.cfg.policies
      } })
    });
    const raw = await response.text();
    let data;
    try { data = JSON.parse(raw); } catch { throw new Error(response.ok ? 'AI service returned an invalid response' : `AI service unavailable (${response.status})`); }
    if (!response.ok || !data.ok) throw new Error(data.error || 'AI service unavailable');
    return data.data;
  }

  friendlyError(error) {
    const message = String(error?.message || '');
    if (error instanceof TypeError || /failed to fetch|networkerror|load failed|network request failed/i.test(message)) {
      return this.labels().networkError;
    }
    return this.labels().serviceError;
  }

  addMessage(text, role = 'assistant') {
    const message = document.createElement('div');
    message.className = `nova-ai__message nova-ai__message--${role}`;
    message.textContent = text;
    if (role === 'assistant' || role === 'loading') {
      const row = document.createElement('div');
      row.className = 'nova-ai__message-row';
      const avatar = document.createElement('span');
      avatar.className = 'nova-ai__message-avatar';
      avatar.setAttribute('aria-hidden', 'true');
      if (this.logoTemplate?.content) avatar.append(this.logoTemplate.content.cloneNode(true));
      row.append(avatar, message);
      this.messages.append(row);
    } else {
      this.messages.append(message);
    }
    this.scrollThread();
    return message;
  }

  scrollThread() {
    requestAnimationFrame(() => { this.querySelector('.nova-ai__body').scrollTop = this.querySelector('.nova-ai__body').scrollHeight; });
  }

  safeUrl(value) {
    try {
      const url = new URL(String(value || ''), location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
    } catch { return ''; }
  }

  text(tag, className, value) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = String(value || '');
    return element;
  }

  formatPrice(product) {
    if (product.priceFormatted) return product.priceFormatted;
    if (!Number.isFinite(Number(product.priceCents))) return '';
    try {
      return new Intl.NumberFormat(this.cfg.locale || 'en', { style: 'currency', currency: product.currencyCode || this.cfg.currency || 'USD' }).format(Number(product.priceCents) / 100);
    } catch { return String(Number(product.priceCents) / 100); }
  }

  createProductCard(product) {
    const labels = this.labels();
    const url = this.safeUrl(product.url || `/products/${encodeURIComponent(product.handle || '')}`);
    if (!url || !product.title) return null;
    const card = document.createElement('a');
    card.className = 'nova-ai__product-card';
    card.href = url;
    card.setAttribute('aria-label', `${labels.view}: ${product.title}`);

    const media = this.text('span', 'nova-ai__product-media', '');
    const imageUrl = this.safeUrl(product.image?.url);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = String(product.image?.alt || product.title).slice(0, 240);
      image.loading = 'lazy';
      image.width = 116;
      image.height = 142;
      media.append(image);
    } else {
      media.append(this.text('span', 'nova-ai__product-placeholder', product.title.charAt(0).toUpperCase()));
    }
    media.append(this.text('span', 'nova-ai__product-index', String((this._cardIndex || 0) + 1).padStart(2, '0')));
    this._cardIndex = (this._cardIndex || 0) + 1;

    const content = this.text('span', 'nova-ai__product-content', '');
    const meta = this.text('span', 'nova-ai__product-meta', '');
    meta.append(this.text('span', '', product.vendor || product.type || labels.selected));
    meta.append(this.text('span', product.available ? 'is-available' : 'is-unavailable', product.available ? labels.inStock : labels.outOfStock));
    content.append(meta, this.text('strong', 'nova-ai__product-title', product.title));
    if (product.reason) {
      const reason = this.text('span', 'nova-ai__product-reason', '');
      reason.append(this.text('small', '', labels.why), this.text('span', '', product.reason));
      content.append(reason);
    } else if (product.description) {
      content.append(this.text('span', 'nova-ai__product-summary', product.description));
    }
    const price = this.text('span', 'nova-ai__product-price-row', '');
    price.append(this.text('b', '', this.formatPrice(product)));
    if (product.compareAtPriceFormatted && Number(product.compareAtPriceCents) > Number(product.priceCents)) price.append(this.text('s', '', product.compareAtPriceFormatted));
    content.append(price);
    const sizes = Array.isArray(product.availableSizes) ? product.availableSizes.slice(0, 5) : [];
    if (sizes.length) {
      const row = this.text('span', 'nova-ai__product-options', '');
      row.append(this.text('small', '', labels.sizes));
      sizes.slice(0, 4).forEach(size => row.append(this.text('b', '', size)));
      if (sizes.length > 4) row.append(this.text('i', '', `+${sizes.length - 4}`));
      content.append(row);
    }
    const footer = this.text('span', 'nova-ai__product-footer', '');
    footer.append(this.text('span', '', labels.view), this.text('b', '', '↗'));
    content.append(footer);
    card.append(media, content);
    return card;
  }

  renderCommerce(host, data) {
    const products = Array.isArray(data?.products) ? data.products : Array.isArray(data?.recommendations) ? data.recommendations : [];
    if (products.length) {
      const deck = this.text('div', 'nova-ai__product-deck', '');
      this._cardIndex = 0;
      products.slice(0, 4).forEach(product => { const card = this.createProductCard(product); if (card) deck.append(card); });
      if (deck.children.length) host.append(deck);
    }
    const sources = Array.isArray(data?.sources) ? data.sources : [];
    if (sources.length) {
      const sourceList = this.text('div', 'nova-ai__sources', '');
      sources.slice(0, 4).forEach(source => {
        const url = this.safeUrl(source.url);
        if (!url) return;
        const link = document.createElement('a');
        link.className = 'nova-ai__source-card';
        link.href = url;
        link.append(this.text('span', 'nova-ai__source-mark', '§'));
        const copy = this.text('span', '', '');
        copy.append(this.text('small', '', this.labels().source), this.text('strong', '', source.label));
        link.append(copy, this.text('b', '', '↗'));
        sourceList.append(link);
      });
      host.append(sourceList);
    }
    const replies = Array.isArray(data?.quickReplies) ? data.quickReplies : [];
    if (replies.length && this.starters) {
      const buttons = [];
      replies.slice(0, 3).forEach(reply => {
        const button = this.text('button', '', reply);
        button.type = 'button';
        button.dataset.aiQuestion = reply;
        buttons.push(button);
      });
      this.starters.replaceChildren(...buttons);
      this.starters.hidden = false;
    }
  }

  renderAssistant(host, data) {
    host.className = 'nova-ai__message nova-ai__message--assistant nova-ai__message--rich';
    host.textContent = '';
    const products = Array.isArray(data?.products) ? data.products : Array.isArray(data?.recommendations) ? data.recommendations : [];
    if (products.length) {
      const guide = this.text('div', 'nova-ai__guide', '');
      guide.append(this.text('span', 'nova-ai__guide-mark', '✦'));
      const heading = this.text('span', 'nova-ai__guide-heading', '');
      heading.append(this.text('small', '', 'SHOPPING GUIDE'), this.text('strong', '', 'Product recommendations'));
      guide.append(heading);
      host.append(guide);
    }
    host.append(this.text('p', 'nova-ai__message-copy', data?.answer || data?.message || this.labels().fallback));
    this.renderCommerce(host, data || {});
    this.scrollThread();
  }

  async chat(event) {
    event.preventDefault();
    const input = event.currentTarget.querySelector('textarea');
    const question = input.value.trim();
    if (!question) return;
    input.value = '';
    this.resizeComposer(input);
    await this.sendQuestion(question);
  }

  async sendQuestion(question) {
    const value = String(question || '').trim().slice(0, 800);
    if (!value || this._asking) return;
    this._asking = true;
    if (this.sendButton) this.sendButton.disabled = true;
    if (this.starters) this.starters.hidden = true;
    this.addMessage(value, 'user');
    const loading = this.addMessage(this.labels().thinking, 'loading');
    try {
      const catalog = await this.catalogFor(value);
      const data = await this.api('chat', { question: value, history: this.history, _catalog: catalog });
      this.renderAssistant(loading, data);
      this.history = [...this.history, { role: 'user', content: value }, { role: 'assistant', content: data.answer || '' }].slice(-8);
      this.track('chat_completed');
    } catch (error) {
      console.error('[No Ask] AI request failed', error);
      loading.className = 'nova-ai__message nova-ai__message--assistant';
      loading.textContent = this.friendlyError(error);
      if (this.starters) this.starters.hidden = false;
    } finally {
      this._asking = false;
      if (this.sendButton) this.sendButton.disabled = false;
    }
  }

  formObject(form) { return Object.fromEntries(new FormData(form).entries()); }

  renderResult(host, data) {
    host.textContent = '';
    const answer = data.recommended_size ? `Recommended size: ${data.recommended_size}\n${(data.fit_notes || []).join('\n')}` : (data.answer || data.message || JSON.stringify(data, null, 2));
    host.append(this.text('div', 'nova-ai__result-copy', answer));
    this.renderCommerce(host, data);
  }

  track(event) {
    const base = String(this.cfg.endpoint || '').replace(/\/$/, '');
    fetch(`${base}/api/events`, { method: 'POST', headers: { 'content-type': 'application/json', 'x-nova-token': this.cfg.token }, body: JSON.stringify({ event, page: this.cfg.page, product_id: this.cfg.product?.id || null }) }).catch(() => {});
  }

  async size(event) {
    event.preventDefault();
    const host = this.querySelector('[data-ai-size-result]');
    host.textContent = 'Finding your best fit…';
    const measurements = this.formObject(event.currentTarget);
    if (this.cfg.rememberProfile) localStorage.setItem('nova-ai-fit', JSON.stringify(measurements));
    try { this.renderResult(host, await this.api('size', { measurements })); this.track('size_completed'); } catch (error) { console.error('[No Ask] Size request failed', error); host.textContent = this.friendlyError(error); }
  }

  async style(event) {
    event.preventDefault();
    const host = this.querySelector('[data-ai-style-result]');
    host.textContent = 'Building your look…';
    const question = `Create an outfit for ${event.currentTarget.occasion.value}. Preferences: ${event.currentTarget.preferences.value}`;
    try {
      const data = await this.api('chat', { question, history: this.history, _catalog: await this.catalogFor(question) });
      host.textContent = '';
      host.append(this.text('div', 'nova-ai__result-copy', data.answer || ''));
      this.renderCommerce(host, data);
      this.track('style_completed');
    } catch (error) { console.error('[No Ask] Style request failed', error); host.textContent = this.friendlyError(error); }
  }

  async personalize() {
    try {
      const data = await this.api('personalize', { visitor: { referrer: document.referrer ? new URL(document.referrer).hostname : 'direct', device: matchMedia('(max-width:749px)').matches ? 'mobile' : 'desktop' }, page_blocks: ['product','story','reviews','related'] });
      this.dispatchEvent(new CustomEvent('nova:ai-personalized', { bubbles: true, detail: data }));
      if (data.message) this.messages?.setAttribute('data-personalized-message', data.message);
    } catch { /* Personalization is non-blocking. */ }
  }
}

if (!customElements.get('nova-ai-suite')) customElements.define('nova-ai-suite', NovaAISuite);
